// @givmo/app-charity — back-office enrollment route constants (CBO-IN3).
//
// The route-constant convention from `features/compliance/compliance-routes.ts`: paths live here so
// the router, the dashboard's getting-started step, and every in-feature link agree on one spelling.
//
// ⚠ THE NOUN IS TAKEN TWICE ELSEWHERE IN THIS ORG, IN TWO OTHER SENSES. Neither is this one, and the
// distinction is worth naming because all three are one word away from each other in a search:
//
//   • SECOND FACTOR — `/board-director/charities/{id}/step-up/totp/enrollment` on the backend, and
//     the matching `apps/board` surface, use "enrollment" for enrolling an AUTHENTICATOR. That is one
//     director registering a device, on a different app, against a different backend principal.
//   • EMPLOYER PROGRAMS — `apps/admin`'s enrollment export is about EMPLOYEES joining a company's
//     donation-match program. Its subject is a person, not an organization.
//
// Here it means one organization being set up in the back office, once. Inside `apps/charity` the
// noun is unclaimed, and the server path stays `/charity-admin/enrollment/...` so a single vocabulary
// spans the seam rather than the console and the API naming the same act two ways.
//
// ⚠ THE ARTIFACT THIS PRODUCES IS THE **ONBOARDING RECORD**, in rendered copy and in code. The
// alternative spellings are banned app-wide, and `enrollment-copy-law.test.ts` is the only file in
// this feature permitted to write them down — naming them here would put them in the very source the
// scan reads.

export const ENROLLMENT_ROUTES = {
  /** The organization's back-office setup: its live record, or the way to start one. */
  home: '/enrollment',
  /** One record: its details, what it is waiting on, and its event log. */
  record: (enrollmentId: string): string => `/enrollment/${enrollmentId}`,
  /**
   * The intake doors for one record (CBO-IN4) — where history gets brought in.
   *
   * ⚠ ITS OWN ADDRESS, NOT A SECTION OF THE RECORD PAGE, and the reason is what the two surfaces
   * ARE. The record page is the account of a lifecycle that finishes; this is an open-ended standing
   * surface that never does. Folding one into the other would put a page with no end state inside a
   * page whose whole subject is reaching one.
   *
   * It also costs no shell diff at all: `app-shell.tsx` titles everything under `/enrollment/`
   * already, so this route inherits its title without touching the hottest file in the app.
   */
  backfill: (enrollmentId: string): string => `/enrollment/${enrollmentId}/backfill`,
} as const;

/**
 * The router param templates for the record drill-down and its doors.
 *
 * ⚠ Registered AFTER the literal `/enrollment` in `routes.tsx`, or the literal is swallowed as an id.
 */
export const ENROLLMENT_RECORD_PATH = '/enrollment/$enrollmentId';
export const ENROLLMENT_BACKFILL_PATH = '/enrollment/$enrollmentId/backfill';
